import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import RecurringTaskModal from '../../../components/ui/RecurringTaskModal';

const CreateTaskModal = ({ familyMembers, onClose, onCreate }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [recurringConfig, setRecurringConfig] = useState(null);
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const priorityOptions = [
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const handleToggleMember = (memberId) => {
    setSelectedMembers(prev => 
      prev?.includes(memberId)
        ? prev?.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMembers?.length === familyMembers?.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(familyMembers?.map(m => m?.id));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!title?.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!description?.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!dueDate) {
      newErrors.dueDate = 'Due date is required';
    }
    
    if (selectedMembers?.length === 0) {
      newErrors.members = 'Select at least one team member';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!validate()) return;
    
    onCreate({
      title: title?.trim(),
      description: description?.trim(),
      dueDate,
      priority,
      assignedMembers: selectedMembers,
      recurringConfig
    });
  };

  // Generate gradient for preview
  const generateMemberGradient = () => {
    const selectedMembersData = familyMembers?.filter(m => selectedMembers?.includes(m?.id));
    
    if (selectedMembersData?.length === familyMembers?.length) {
      return 'linear-gradient(135deg, #EF4444 0%, #F97316 14%, #F59E0B 28%, #84CC16 42%, #10B981 56%, #3B82F6 70%, #8B5CF6 84%, #EC4899 100%)';
    } else if (selectedMembersData?.length > 1) {
      const colors = selectedMembersData?.map(m => m?.color)?.join(', ');
      return `linear-gradient(135deg, ${colors})`;
    } else if (selectedMembersData?.length === 1) {
      return selectedMembersData?.[0]?.color;
    }
    return '#E5E7EB';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-xl max-w-2xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Create Multi-Member Task</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Assign a collaborative task to multiple family members
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="x" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Task Title *
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e?.target?.value)}
              placeholder="e.g., Spring Garden Cleanup"
              error={errors?.title}
            />
            {errors?.title && (
              <p className="text-xs text-destructive mt-1">{errors?.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e?.target?.value)}
              placeholder="Describe what needs to be done..."
              className="w-full min-h-[100px] px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
            {errors?.description && (
              <p className="text-xs text-destructive mt-1">{errors?.description}</p>
            )}
          </div>

          {/* Due Date and Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Due Date *
              </label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e?.target?.value)}
                error={errors?.dueDate}
                min={new Date()?.toISOString()?.split('T')?.[0]}
              />
              {errors?.dueDate && (
                <p className="text-xs text-destructive mt-1">{errors?.dueDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Priority *
              </label>
              <Select
                value={priority}
                onChange={(e) => setPriority(e?.target?.value)}
                options={priorityOptions}
              />
            </div>
          </div>

          {/* Recurring Schedule */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Recurring Schedule (Optional)
            </label>
            <Button
              type="button"
              variant={recurringConfig ? 'default' : 'outline'}
              onClick={() => setIsRecurringModalOpen(true)}
              className="w-full"
            >
              <Icon name="repeat" size={16} className="mr-2" />
              {recurringConfig 
                ? `Repeats ${recurringConfig?.frequency}` 
                : 'Set Recurring Schedule'}
            </Button>
            {recurringConfig && (
              <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3 mt-2">
                <p className="text-sm text-muted-foreground">
                  {recurringConfig?.frequency === 'daily' && 'Repeats every day'}
                  {recurringConfig?.frequency === 'weekly' && 
                    `Repeats every ${recurringConfig?.pattern?.daysOfWeek?.join(', ')}`}
                  {recurringConfig?.frequency === 'monthly' && 
                    `Repeats on day ${recurringConfig?.pattern?.dayOfMonth} of each month`}
                  {recurringConfig?.endDate && ` until ${new Date(recurringConfig?.endDate)?.toLocaleDateString()}`}
                </p>
                <button
                  type="button"
                  onClick={() => setRecurringConfig(null)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="x" size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Member Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-foreground">
                Assign Team Members *
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedMembers?.length === familyMembers?.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            {/* Color Preview */}
            {selectedMembers?.length > 0 && (
              <div className="mb-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-medium text-foreground">Team Color Preview:</span>
                  {selectedMembers?.length === familyMembers?.length && (
                    <span className="text-sm">✨ Everyone Selected!</span>
                  )}
                </div>
                <div
                  className="h-8 rounded-lg"
                  style={{ background: generateMemberGradient() }}
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {familyMembers?.map(member => (
                <div
                  key={member?.id}
                  onClick={() => handleToggleMember(member?.id)}
                  className="flex items-center gap-3 p-3 bg-background border border-border rounded-lg cursor-pointer hover:bg-muted transition-colors"
                >
                  <Checkbox
                    checked={selectedMembers?.includes(member?.id)}
                    onChange={() => handleToggleMember(member?.id)}
                  />
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                    style={{ backgroundColor: member?.color }}
                  >
                    {member?.initials}
                  </div>
                  <div>
                    <div className="font-medium text-foreground">{member?.name}</div>
                    <div className="text-xs text-muted-foreground">{member?.role}</div>
                  </div>
                </div>
              ))}
            </div>
            {errors?.members && (
              <p className="text-xs text-destructive mt-2">{errors?.members}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" fullWidth onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" fullWidth iconName="plus">
              Create Task
            </Button>
          </div>
        </form>
      </div>

      {/* Recurring Task Modal */}
      {isRecurringModalOpen && (
        <RecurringTaskModal
          onClose={() => setIsRecurringModalOpen(false)}
          onSave={(data) => {
            setRecurringConfig(data);
            setIsRecurringModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default CreateTaskModal;